import { ReportDraft, StatusResponse, SubmitReportRequest, Player } from '../types/matchReport.ts';
import { formatJerseyNumber } from './formatUtils';
import unifiedApi from './unifiedApi';

export class MatchReportAPI {
  /**
   * Save a draft report without submitting
   * Note: Draft functionality disabled - no local storage
   */
  static async saveDraft(_gameId: number, _draft: ReportDraft, _teamId: number): Promise<void> {
    // Draft functionality disabled
    return Promise.resolve();
  }

  /**
   * Submit a match report
   */
  static async submitReport(gameId: number, draft: ReportDraft, teamId: number): Promise<StatusResponse> {
    const payload = this.toReportPayload(draft, teamId);
    const requestId = crypto.randomUUID();

    try {
      await unifiedApi.submitMatchReport(gameId, payload, requestId);
    } catch (error: any) {
      throw new Error(`Failed to submit report: ${error?.message ?? error}`);
    }

    // After successful submission, fetch the latest status
    return this.fetchStatus(gameId, teamId);
  }

  /**
   * Update an existing match report (league admin+ only)
   */
  static async editReport(gameId: number, reportId: number, draft: ReportDraft, teamId: number): Promise<StatusResponse> {
    const payload = this.toReportPayload(draft, teamId);

    await unifiedApi.updateMatchReport(gameId, reportId, payload);

    // After successful update, fetch the latest status
    return this.fetchStatus(gameId, teamId);
  }

  /**
   * Fetch the current game status and reconciliation information
   */
  static async fetchStatus(gameId: number, teamId?: number): Promise<StatusResponse> {
    const data = await unifiedApi.getGameMatchReports(gameId);
    if (!data) {
      throw new Error('Failed to fetch game status');
    }
    return this.mapStatusResponse(data, teamId);
  }

  /**
   * Fetch team roster for the game
   */
  static async fetchTeamRoster(teamId: number): Promise<Player[]> {
    const players = await unifiedApi.getTeamPlayers(teamId);
    if (!players) {
      throw new Error('Failed to fetch team roster');
    }
    
    // Transform the response to include computed name property
    return players.map((player: any) => ({
      ...player,
      userTenantId: player.id, // Map id to userTenantId
      name: `${player.firstName} ${player.lastName}`,
      // Ensure shirtNumber is available for jersey number display (formatted to remove decimals)
      shirtNumber: formatJerseyNumber(player.jerseyNumber)
    }));
  }

  /**
   * Fetch player RSVPs for the game, keyed by userTenantId.
   * Only team/player RSVPs are used here - spare availability is a separate mechanism.
   * Returns an empty map when there are no RSVPs; throws if the request fails.
   */
  static async fetchGameRsvpMap(gameId: number): Promise<Map<number, boolean>> {
    const rsvps = await unifiedApi.getGameRsvps(gameId);
    const map = new Map<number, boolean>();
    (rsvps || []).forEach((rsvp: any) => {
      map.set(Number(rsvp.userTenantId), rsvp.attending === true);
    });
    return map;
  }

  /**
   * Convert UI draft to API payload format
   */
  static toReportPayload(draft: ReportDraft, teamId: number): SubmitReportRequest {
    const scorers = draft.events
      .filter(event => event.eventType === 'Goal')
      .map(event => ({
        userTenantId: event.userTenantId,
        minute: event.minute,
        period: event.minute && event.minute > 45 ? 2 : 1,
        assistUserTenantId: event.relatedUserTenantId,
        notes: event.notes,
      }));

    const cards = draft.events
      .filter(event => ['YellowCard', 'RedCard', 'SecondYellowRed'].includes(event.eventType))
      .map(event => ({
        userTenantId: event.userTenantId,
        cardType: event.eventType as "YellowCard" | "RedCard" | "SecondYellowRed",
        minute: event.minute,
        period: event.minute && event.minute > 45 ? 2 : 1,
        notes: event.notes,
      }));

    const otherEvents = draft.events
      .filter(event => ['CleanSheet', 'Injury'].includes(event.eventType))
      .map(event => ({
        userTenantId: event.userTenantId,
        eventType: event.eventType as "CleanSheet" | "Injury",
        minute: event.minute,
        period: event.minute && event.minute > 45 ? 2 : 1,
        notes: event.notes,
      }));

    const appearances = draft.appearances
      .filter(app => app.played)
      .map(app => ({
        userTenantId: app.userTenantId,
        isSpare: app.isSpare,
        shirtNumber: app.shirtNumber ? parseInt(app.shirtNumber) : undefined,
        started: true, // Default assumption
        onMinute: undefined,
        offMinute: undefined,
        notes: undefined,
      }));

    return {
      submittingTeamId: teamId,
      homeGoals: draft.homeGoals || 0,
      awayGoals: draft.awayGoals || 0,
      scorers,
      cards,
      otherEvents,
      appearances,
      notes: draft.notes,
    };
  }

  /**
   * Map API response to our status interface
   */
  private static mapStatusResponse(data: any, teamId?: number): StatusResponse {
    // Determine if the submitting team is the home team
    const isHomeTeam = teamId ? data.homeTeamId === teamId : true;
    
    // Determine which reports belong to "your team" vs "opponent team"
    const yourTeamReport = isHomeTeam ? data.homeTeamReport : data.awayTeamReport;
    const opponentTeamReport = isHomeTeam ? data.awayTeamReport : data.homeTeamReport;
    
    return {
      yourReport: {
        status: yourTeamReport ? "Submitted" : "Missing",
        homeGoals: yourTeamReport?.homeGoals,
        awayGoals: yourTeamReport?.awayGoals,
      },
      opponentReport: {
        status: opponentTeamReport ? "Submitted" : "Missing",
        homeGoals: opponentTeamReport?.homeGoals,
        awayGoals: opponentTeamReport?.awayGoals,
      },
      reconciliationStatus: data.scoresMatch ? "Matched" : data.bothReportsSubmitted ? "Mismatch" : "Waiting",
      gameStatus: data.gameFinalized ? "Finalized" : "Pending",
      gameInfo: {
        homeTeamName: data.homeTeamName || "",
        awayTeamName: data.awayTeamName || "",
        gameDate: data.gameDate || "",
        isHomeTeam: isHomeTeam,
      },
    };
  }
}

/**
 * Validation utilities
 */
export function validateSecondYellow(events: any[], userTenantId: number): boolean {
  const hasYellowCard = events.some(
    event => event.eventType === 'YellowCard' && event.userTenantId === userTenantId
  );
  return hasYellowCard;
}
